import { createOrg } from "../../../services/auth";
import { useMutation, useQueryClient } from "react-query";
import * as Helpers from "../../../utils/helpers";
import { useDispatch } from "react-redux";
import { actions as AuthActions } from "../../../store/authRedux/actions";
import { switchOrganization } from "../../../utils/orgSwitch";
import {
  Col,
  Form,
  Input,
  Modal,
  notification,
  Row,
  Select,
  Typography,
  Upload,
} from "antd";
import { PlusOutlined, ShopOutlined } from "@ant-design/icons";
import { ModalTitle } from "../../../components";
import { INDUSTRY_OPTIONS } from "../../../constants";
import * as Yup from "yup";
import { useFormik } from "formik";
import { serialize } from "object-to-formdata";
import { useMediaQuery } from "../../../hooks";
import styles from "./styles.module.css";

/* Creating a second workspace from the header asks exactly what the signup
 * screen asks — name, logo, what the team does — and for the same reason: email,
 * phone and postal address were four optional fields standing between someone
 * and a board they had already decided to build. They live in فضای کاری › سازمان
 * now.
 *
 * Nothing here finishes the setup: `switchOrganization` reloads into the new
 * workspace, which has no `onboarded_at` yet, so the wizard picks it up at the
 * project step exactly as it does after signup. */
const validationSchema1 = Yup.object({
  title: Yup.string().required("وارد کردن این فیلد الزامی است"),
  industry_id: Yup.number().required("وارد کردن این فیلد الزامی است"),
  avatar: Yup.mixed().notRequired().nullable(),
});

const initialValues = {
  title: undefined,
  industry_id: undefined,
  avatar: undefined,
  color_code: "#1677ff",
};

const RegisterOrgModal = ({ isOpen, setIsOpen }) => {
  const isMobile = useMediaQuery("(max-width: 992px)");

  const dispatch = useDispatch();

  const queryClient = useQueryClient();

  const onSubmit = (values) => {
    createOrgMutate.mutate({
      body: serialize(Helpers.removeEmptyFString(values)),
    });
  };

  const formik = useFormik({
    initialValues,
    validationSchema: validationSchema1,
    onSubmit,
    validateOnChange: false,
    validateOnBlur: true,
  });

  const createOrgMutate = useMutation(createOrg, {
    onSuccess: (res) => {
      const org = res.organization;
      notification.success({ className: "ds-notif", message: "خوش آمدید!" });
      dispatch(AuthActions.setAuthenticated(true));
      setIsOpen(false);

      switchOrganization(org, queryClient, dispatch);
    },
    onError: (error) => {},
  });

  const formLoading = createOrgMutate.isLoading;

  return (
    <Modal
      className="ds-modal"
      title={
        <ModalTitle
          icon={<ShopOutlined />}
          title="ایجاد سازمان جدید"
          subtitle="شما ادمین این سازمان می‌شید و می‌تونید بقیه رو دعوت کنید."
        />
      }
      open={isOpen}
      onCancel={() => setIsOpen(false)}
      width={isMobile ? "95vw" : 560}
      centered
      confirmLoading={formLoading}
      onOk={() => formik.submitForm()}
      okText="ادامه"
      cancelText="انصراف"
    >
      <Row gutter={[12, 4]}>
        <Col xs={24}>
          <div className={styles.avatarBlock}>
            <Upload
              name="profile"
              listType="picture-circle"
              className="profile-uploader-org"
              showUploadList={false}
              accept="image/png, image/gif, image/jpeg"
              customRequest={(event) => {
                formik.setFieldValue("avatar", event.file);
              }}
            >
              {formik.values.avatar ? (
                <img
                  src={URL.createObjectURL(formik.values.avatar)}
                  alt="avatar"
                  className={styles.avatarPreview}
                />
              ) : (
                <PlusOutlined />
              )}
            </Upload>
            <span className={styles.avatarHint}>
              لوگو یا عکس سازمان (اختیاری)
            </span>
          </div>
        </Col>

        <Col xs={24}>
          <Form.Item
            required
            label="نام سازمان"
            className="mb-1 w-100"
            labelCol={{ span: 24 }}
          >
            <Input
              size="large"
              placeholder="مثلاً تیم محصول تسکامون"
              onChange={(e) => formik.setFieldValue("title", e.target.value)}
              onBlur={() => formik.setFieldTouched("title", true)}
              onPressEnter={formik.submitForm}
            />
            {formik.errors.title && formik.touched.title && (
              <Typography.Text type="danger" className={styles.fieldError}>
                {formik.errors.title}
              </Typography.Text>
            )}
          </Form.Item>
        </Col>

        <Col xs={24}>
          <Form.Item
            required
            label="حوزه‌ی فعالیت"
            className="mb-1 w-100"
            labelCol={{ span: 24 }}
          >
            <Select
              size="large"
              className="w-100"
              showSearch
              allowClear
              placeholder="حوزه‌ی فعالیت تیمت رو انتخاب کن"
              options={INDUSTRY_OPTIONS}
              value={formik.values.industry_id}
              optionFilterProp="label"
              onChange={(value) => formik.setFieldValue("industry_id", value)}
              onBlur={() => formik.setFieldTouched("industry_id", true)}
            />
            {formik.errors.industry_id && formik.touched.industry_id && (
              <Typography.Text type="danger" className={styles.fieldError}>
                {formik.errors.industry_id}
              </Typography.Text>
            )}
            <span className={styles.fieldHint}>
              بر اساس این انتخاب، قالب‌های آماده‌ی همین حوزه توی فضای کاریت ساخته می‌شن.
            </span>
          </Form.Item>
        </Col>
      </Row>
    </Modal>
  );
};

export default RegisterOrgModal;
